import React, { useEffect, useState } from "react";
import { Input, Modal, Select, Flex, Form, notification, Spin } from "antd";
import { employee, resource } from "../api";
import { userRole, employeeId } from "../utils/userDetails";

const { Option } = Select;

const ResourceEditModal = ({ isOpen, onClose, record }) => {
  const [form] = Form.useForm();
  const [employeeList, setEmployeeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [primarySkills, setPrimarySkills] = useState([]);
  const [secondarySkills, setSecondarySkills] = useState([]);
  const [selectOption, setSelectOption] = useState([]);
  const [secondarySelectOption, setSecondarySelectOption] = useState([]);

  useEffect(() => {
    if (isOpen && record) {
      handleGetResourceDetails(record.employeeNo);
      getAllEmployeeList();
    }
  }, [isOpen, record]);

  const handleGetResourceDetails = async (employeeNo) => {
    try {
      setLoading(true);
      const response = await resource.getResourceDetails({ employeeNo });
      const apiData = response.data;

      form.setFieldsValue({
        team: apiData.team,
        workingLocation: apiData.workingLocation,
        available: apiData.available ? "Yes" : "No",
        at_client: apiData.atClient ? "Yes" : "No",
        work_mode: apiData.workMode || "Remote",
        preferred_location: apiData.preferredLocation,
        primary_skills: apiData.primary_skills || [],
        secondary_skills: apiData.secondary_skills || [],
        reporting_to: apiData.managerNo,
        remarks: apiData.remarks,
      });

      setPrimarySkills(apiData.primary_skills || []);
      setSecondarySkills(apiData.secondary_skills || []);
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const getAllEmployeeList = async () => {
    try {
      const response = await employee.getEmployeeList();
      const newResponse = response.data.map((employee) => {
        return {
          value: employee.employeeNo,
          label: `${employee.name}`,
        };
      });
      setEmployeeList(newResponse);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handlePrimarySkills = (values) => {
    const filtered = values
      .filter((v) => v && v.trim() !== "")
      .map((v) => v.trim());

    const unique = [...new Set(filtered)];
    setPrimarySkills(unique);
  };

  const handleSecondarySkills = (values) => {
    const filtered = values
      .filter((v) => v && v.trim() !== "")
      .map((v) => v.trim());

    const unique = [...new Set(filtered)];
    setSecondarySkills(unique);
  };

  const skillsSearch = async (val) => {
    try {
      if (val) {
        const response = await employee.getSkills(`?search=${val}`);
        setSelectOption(response?.data || []);
        setSecondarySelectOption(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching skills", error);
    }
  };

  const handleInputChange = (inputValue) => {
    if (inputValue.trim().length >= 3) {
      skillsSearch(inputValue.trim());
    } else if (inputValue.trim().length < 3) {
      setSelectOption([]);
      setSecondarySelectOption([]);
    }
  };

  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        setUpdateLoading(true);
        const payload = {
          ...values,
          employeeNo: record.employeeNo || employeeId,
          reporting_to: values.reporting_to,
          primary_skills: primarySkills || values.primary_skills,
          secondary_skills: secondarySkills || values.secondary_skills,
        };

        resource
          .updateResourceDetails({ resourceDetails: payload })
          .then((response) => {
            console.log("API Response:", response.data);
            notification.success({
              message: "Resource details updated successfully",
            });
            setUpdateLoading(false);
            form.resetFields();
            onClose();
          })
          .catch((error) => {
            console.log("Error updating resource details:", error);
            notification.error({
              message: "Failed to update resource details",
            });
            setUpdateLoading(false);
          });
      })
      .catch((error) => {
        console.error("Validation Failed:", error);
        notification.error({
          message: "Please fill all required fields",
        });
        setUpdateLoading(false);
      });
  };

  return (
    <Modal
      title="Edit Resource"
      open={isOpen}
      onOk={handleSave}
      onCancel={onClose}
      confirmLoading={updateLoading}
      width="40vw"
      okText="Save"
      cancelText="Cancel"
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Flex gap="15px">
            <Form.Item
              label="Work Location"
              name="workingLocation"
              rules={[{ required: true, message: "Please enter Location" }]}
              style={{ width: "100%" }}
            >
              <Input disabled={userRole == "admin" ? false : true} />
            </Form.Item>

            <Form.Item
              label="Team"
              name="team"
              rules={[{ required: true, message: "Please enter Team" }]}
              style={{ width: "100%" }}
            >
              <Select disabled={userRole == "admin" ? false : true}>
                <Option value="5G">5G</Option>
                <Option value="AI/ML">AI/ML</Option>
                <Option value="Apps">Apps</Option>
                <Option value="BA">BA</Option>
                <Option value="Cloud">Cloud</Option>
                <Option value="Embd.">Embd.</Option>
                <Option value="Finance">Finance</Option>
                <Option value="GNSS">GNSS</Option>
                <Option value="HR">HR</Option>
                <Option value="Ops">Ops</Option>
                <Option value="PP">PP</Option>
                <Option value="QA">QA</Option>
                <Option value="TA">TA</Option>
                <Option value="UI/UX">UI/UX</Option>
                <Option value="US TA">US TA</Option>
              </Select>
            </Form.Item>
          </Flex>

          <Form.Item
            label="Primary Skills"
            name="primary_skills"
            style={{ width: "100%" }}
          >
            <Select
              mode="tags"
              onChange={handlePrimarySkills}
              onSearch={handleInputChange}
              placeholder="Enter your skills"
              style={{ width: "100%" }}
              allowClear
              filterOption={false}
              onInputKeyDown={(e) => {
                const value = e.target.value;

                if (e.key === "Enter") {
                  e.preventDefault();
                  if (value && value.trim() !== "") {
                    const commaSeparated = value
                      .split(",")
                      .map((v) => v.trim())
                      .filter((v) => v !== "");

                    if (commaSeparated.length > 0) {
                      handlePrimarySkills([
                        ...primarySkills,
                        ...commaSeparated,
                      ]);
                    }
                    e.target.value = "";
                  }
                }
              }}
              onPaste={(e) => {
                const paste = e.clipboardData.getData("text");
                if (paste.includes(",")) {
                  e.preventDefault();
                  const commaSeparated = paste
                    .split(",")
                    .map((v) => v.trim())
                    .filter((v) => v !== "");

                  if (commaSeparated.length > 0) {
                    const updatedSkills = [
                      ...new Set([...primarySkills, ...commaSeparated]),
                    ];
                    handlePrimarySkills(updatedSkills);

                    const newOptions = commaSeparated
                      .filter(
                        (skill) =>
                          !selectOption.some((opt) => opt.skill === skill)
                      )
                      .map((skill, index) => ({
                        id: `custom-${Date.now()}-${index}`,
                        skill,
                      }));
                    setSelectOption([...selectOption, ...newOptions]);
                  }
                }
              }}
            >
              {selectOption &&
                selectOption.length > 0 &&
                selectOption.map((skill) => (
                  <Option key={skill.id} value={skill.skill}>
                    {skill.skill}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Secondary Skills"
            name="secondary_skills"
            style={{ width: "100%" }}
          >
            <Select
              mode="tags"
              onChange={handleSecondarySkills}
              onSearch={handleInputChange}
              placeholder="Enter your skills"
              style={{ width: "100%" }}
              allowClear
              filterOption={false}
              onInputKeyDown={(e) => {
                const value = e.target.value;
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (value && value.trim() !== "") {
                    const commaSeparated = value
                      .split(",")
                      .map((v) => v.trim())
                      .filter((v) => v !== "");

                    if (commaSeparated.length > 0) {
                      handleSecondarySkills([
                        ...secondarySkills,
                        ...commaSeparated,
                      ]);
                    }
                    e.target.value = "";
                  }
                }
              }}
              onPaste={(e) => {
                const paste = e.clipboardData.getData("text");
                if (paste.includes(",")) {
                  e.preventDefault();

                  const commaSeparated = paste
                    .split(",")
                    .map((v) => v.trim())
                    .filter((v) => v !== "");

                  if (commaSeparated.length > 0) {
                    const updatedSkills = [
                      ...new Set([...secondarySkills, ...commaSeparated]),
                    ];
                    handleSecondarySkills(updatedSkills);

                    const newOptions = commaSeparated
                      .filter(
                        (skill) =>
                          !secondarySelectOption.some(
                            (opt) => opt.skill === skill
                          )
                      )
                      .map((skill, index) => ({
                        id: `secondary-${Date.now()}-${index}`,
                        skill,
                      }));
                    setSecondarySelectOption([
                      ...secondarySelectOption,
                      ...newOptions,
                    ]);
                  }
                }
              }}
            >
              {secondarySelectOption &&
                secondarySelectOption.length > 0 &&
                secondarySelectOption.map((skill) => (
                  <Option key={skill.id} value={skill.skill}>
                    {skill.skill}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Flex gap="15px">
            <Form.Item
              label="Available"
              name="available"
              rules={[
                { required: true, message: "Please select Availability" },
              ]}
              style={{ width: "100%" }}
            >
              <Select disabled={userRole == "admin" ? false : true}>
                <Option value="Yes">Yes</Option>
                <Option value="No">No</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Reporting To"
              name="reporting_to"
              rules={[{ required: true, message: "Please enter Reporting To" }]}
              style={{ width: "100%" }}
            >
              <Select
                showSearch
                placeholder="Select an employee"
                filterOption={filterOption}
                optionFilterProp="children"
                options={employeeList}
                onChange={(value) => {
                  form.setFieldValue("reporting_to", value);
                }}
                disabled={userRole == "admin" ? false : true}
              ></Select>
            </Form.Item>
          </Flex>

          <Flex gap="15px">
            <Form.Item
              label="At Client"
              name="at_client"
              rules={[{ required: true, message: "Please select At Client" }]}
              style={{ width: "100%" }}
            >
              <Select disabled={userRole == "admin" ? false : true}>
                <Option value="Yes">Yes</Option>
                <Option value="No">No</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Work Mode"
              name="work_mode"
              rules={[{ required: true, message: "Please select work mode" }]}
              style={{ width: "100%" }}
            >
              <Select disabled={userRole == "admin" ? false : true}>
                <Option value="Remote">Remote</Option>
                <Option value="Hybrid">Hybrid</Option>
                <Option value="Onsite">Onsite</Option>
              </Select>
            </Form.Item>
          </Flex>

          <Form.Item
            label="Preferred Location"
            name="preferred_location"
            rules={[
              { required: true, message: "Please enter Preferred Location" },
            ]}
          >
            <Input />
          </Form.Item>

          {userRole == "admin" && (
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea rows={4} />
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default ResourceEditModal;
